/**
 * @module common/DualAxis/axisMetricUtils
 * @description 双轴指标 uid 与存储结构归一化
 */

import type {
  ILineDualAxisConfig,
  IMixedAxisConfig,
  TAxisMetric,
} from './types';

export const DEFAULT_TABLE_CODE = 't0';
export const METRIC_UID_SEPARATOR = '@@DATALAB@@';

export const getSafeArray = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

/**
 * 从 QB columnMetrics 项解析指标字段名（与 ChartsDrop cloneDeepMetrics 一致）
 * 优先 value，其次 column_name / column
 */
export const resolveMetricFieldValue = (
  item: Record<string, unknown>,
): string => {
  const raw =
    item.value ?? item.column_name ?? item.column ?? item.metric;
  return raw != null && String(raw).trim() ? String(raw).trim() : '';
};

/** 指标下拉展示文案 */
export const resolveMetricFieldLabel = (
  item: Record<string, unknown>,
  metricValue: string,
): string =>
  String(
    item.column_display ||
      item.column_display_en ||
      item.column_text ||
      item.column_name ||
      item.text ||
      metricValue,
  );

export const getMetricUid = (
  metric: string,
  tableCode = DEFAULT_TABLE_CODE,
): string => `${metric}${METRIC_UID_SEPARATOR}${tableCode}`;

export const toMetricKey = (item: TAxisMetric): string =>
  getMetricUid(item?.metric || '', item?.tableCode || DEFAULT_TABLE_CODE);

export const getMetricDiff = (
  allMetricUids: string[],
  selected: string[],
): string[] => {
  const selectedSet = new Set(selected);
  return allMetricUids.filter((uid) => !selectedSet.has(uid));
};

/** 折线双轴：从存储读取并归一化为 metrics[] */
export const normalizeLineDualAxisFromStorage = (
  value: unknown,
  chartType: string,
): ILineDualAxisConfig => {
  if (!value || typeof value !== 'object') {
    return {
      yAxisName: '',
      metrics: [],
      chartType,
      yAxisRangeSwitch: false,
    };
  }
  const config = value as ILineDualAxisConfig & { metric?: string };
  let metrics = getSafeArray<TAxisMetric>(config.metrics)
    .map((item) => ({
      metric: item?.metric || '',
      tableCode: item?.tableCode || DEFAULT_TABLE_CODE,
    }))
    .filter((item) => !!item.metric);

  if (metrics.length === 0 && typeof config.metric === 'string' && config.metric) {
    metrics = [{ metric: config.metric, tableCode: DEFAULT_TABLE_CODE }];
  }

  return {
    yAxisName: config.yAxisName || '',
    metrics,
    chartType: config.chartType || chartType,
    yAxisRangeSwitch: !!config.yAxisRangeSwitch,
  };
};

const toUiChartType = (chartType: string): string => {
  if (!chartType) return 'chart-bar';
  if (chartType.startsWith('chart-')) return chartType;
  return chartType === 'line' ? 'chart-line' : 'chart-bar';
};

/** 组合图：从存储读取并归一化为 metric 字符串 */
export const normalizeMixedAxisFromStorage = (
  value: unknown,
  oldValue: unknown,
): IMixedAxisConfig => {
  if (!value || typeof value !== 'object') {
    return { yAxisName: '', metric: '', chartType: 'chart-bar' };
  }
  const config = value as IMixedAxisConfig & {
    metrics?: TAxisMetric[] | string[];
  };

  let metric = '';
  if (typeof config.metric === 'string') {
    metric = config.metric;
  } else if (Array.isArray(config.metrics) && config.metrics.length > 0) {
    const first = config.metrics[0];
    if (typeof first === 'string') {
      metric = first;
    } else if (first && typeof first === 'object') {
      metric = (first as TAxisMetric).metric || '';
    }
  }

  return {
    yAxisName: config.yAxisName || '',
    metric,
    chartType: toUiChartType(config.chartType || 'chart-bar'),
    yAxisRangeSwitch: oldValue?.yAxisRangeSwitch ?? config.yAxisRangeSwitch ?? false,
    // 这里旧的组合图中 Y轴范围开关是单独的字段，所以需要兼容旧的逻辑，新的组合图中 Y轴范围开关是和主次轴一起的
  };
};

/** 组合图提交：metric 字符串 + chartType 无 chart- 前缀 */
export const formatMixedAxisForSubmit = (
  axis: unknown,
  fallbackChartType: 'bar' | 'line',
): Record<string, unknown> => {
  const normalized = normalizeMixedAxisFromStorage(axis);
  let chartType = normalized.chartType || `chart-${fallbackChartType}`;
  if (chartType.startsWith('chart-')) {
    chartType = chartType.replace('chart-', '').toLowerCase();
  }
  return {
    yAxisName: normalized.yAxisName?.trim() || '',
    metric: normalized.metric || '',
    chartType,
    yAxisRangeSwitch: !!normalized.yAxisRangeSwitch,
  };
};

const MIXED_LEGACY_TOP_LEVEL_KEYS = [
  'primaryChartType',
  'secondaryChartType',
  'primaryMetric',
  'secondaryMetric',
] as const;

/**
 * 组合图表单态归一化 — 与折线双轴一致，仅保留 primaryYAxis / secondYAxis 对象
 * 兼容旧版扁平字段：primaryYAxis(字符串)、primaryMetric、primaryChartType 等
 */
export const compatMixedChartFormState = (
  config: Record<string, unknown>,
  columnMetrics: unknown[] = [],
): void => {
  const metrics = columnMetrics as Record<string, unknown>[];
  const firstMetric = metrics[0]
    ? resolveMetricFieldValue(metrics[0])
    : '';
  const secondMetric = metrics[1]
    ? resolveMetricFieldValue(metrics[1])
    : '';

  const legacyPrimaryName =
    typeof config.primaryYAxis === 'string' ? config.primaryYAxis : '';
  const legacySecondaryName =
    typeof config.secondaryYAxis === 'string'
      ? config.secondaryYAxis
      : typeof config.secondYAxis === 'string'
        ? config.secondYAxis
        : '';

  const primaryRaw =
    config.primaryYAxis && typeof config.primaryYAxis === 'object'
      ? config.primaryYAxis
      : {
          yAxisName: legacyPrimaryName,
          metric: config.primaryMetric ?? firstMetric,
          chartType: config.primaryChartType,
          yAxisRangeSwitch: config.yAxisRangeSwitch,
        };

  const secondRaw =
    config.secondYAxis && typeof config.secondYAxis === 'object'
      ? config.secondYAxis
      : {
          yAxisName: legacySecondaryName,
          metric: config.secondaryMetric ?? secondMetric,
          chartType: config.secondaryChartType,
          yAxisRangeSwitch: config.yAxisRangeSwitch2,
        };

  // eslint-disable-next-line no-param-reassign
  config.primaryYAxis = normalizeMixedAxisFromStorage(primaryRaw);
  // eslint-disable-next-line no-param-reassign
  config.secondYAxis = normalizeMixedAxisFromStorage(secondRaw);

  for (const key of MIXED_LEGACY_TOP_LEVEL_KEYS) {
    delete config[key];
  }
};

/** 组合图提交重组 — 剔除顶层遗留字段，输出主/次轴对象（对齐 ChartsDrop mixedChartHandler） */
export const formatMixedChartForSubmit = (
  values: Record<string, unknown>,
): Record<string, unknown> => {
  const result = { ...values };

  const formatAxis = (
    axis: unknown,
    legacyChartType: unknown,
    fallback: 'bar' | 'line',
  ): Record<string, unknown> => {
    const axisObj =
      axis && typeof axis === 'object'
        ? { ...(axis as Record<string, unknown>) }
        : {};
    if (legacyChartType && !axisObj.chartType) {
      axisObj.chartType = legacyChartType;
    }
    return formatMixedAxisForSubmit(axisObj, fallback);
  };

  if ('primaryYAxis' in result) {
    result.primaryYAxis = formatAxis(
      result.primaryYAxis,
      result.primaryChartType,
      'bar',
    );
  }
  if ('secondYAxis' in result) {
    result.secondYAxis = formatAxis(
      result.secondYAxis,
      result.secondaryChartType,
      'line',
    );
  }

  for (const key of MIXED_LEGACY_TOP_LEVEL_KEYS) {
    delete result[key];
  }

  return result;
};
